import React, { useRef, useEffect, useState } from 'react';
import './App.css';
import BeltPromotionModal from './BeltPromotionModal';
import PosePreloader from './PosePreloader';
import usePoseLoader from './hooks/usePoseLoader';
import { useEquipmentImages } from './hooks/useEquipmentImages';
import { drawOptimizedSkeleton, drawOptimizedGloves, calculateShoulderDistance } from './utils/skeletonUtils';
import useGameScreen from './hooks/useGameScreen';

function TrainingMode({ userData, onBackToMenu }) {
  useGameScreen();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [countdown, setCountdown] = useState(3);
  const [isReady, setIsReady] = useState(false);
  const [comboCount, setComboCount] = useState(0);
  const comboRef = useRef(0);
  const comboTimer = useRef(null);
  const [feedback, setFeedback] = useState({ show: false, text: '', type: '', x: 0, y: 0, startTime: 0 });
  const [targetZone, setTargetZone] = useState('cara-derecha');
  const targetZoneRef = useRef('cara-derecha');
  const lastHitTime = useRef(0);
  const [particles, setParticles] = useState([]);
  const particlesRef = useRef([]);
  const equipmentImages = useEquipmentImages();
  const [showBeltModal, setShowBeltModal] = useState(false);
  const [beltPromotionData, setBeltPromotionData] = useState(null);
  const audioRef = useRef(null);
  const pegarOponenteRef = useRef(null);
  const onResultsRef = useRef(null);
  const [showEnergyWarning, setShowEnergyWarning] = useState(false);
  const energyCost = 10;

  const zones = {
    'cara-derecha': { img: '/lado/ladacaraderecho.jpg', name: 'Cara Derecha', y: 0.30, xOffset: 0.08, points: 30, color: '#888888' },
    'cara-izquierda': { img: '/lado/ladacaraizquierda.jpg', name: 'Cara Izquierda', y: 0.30, xOffset: -0.08, points: 30, color: '#888888' },
    'cuerpo-derecho': { img: '/lado/ladoderecho.jpg', name: 'Cuerpo Derecho', y: 0.45, xOffset: 0.10, points: 20, color: '#888888' },
    'cuerpo-centro': { img: '/lado/ladocentro.jpg', name: 'Cuerpo Centro', y: 0.45, xOffset: 0, points: 20, color: '#888888' },
    'cuerpo-izquierdo': { img: '/lado/ladoizquierdo.jpg', name: 'Cuerpo Izquierdo', y: 0.45, xOffset: -0.10, points: 20, color: '#888888' }
  };

  const prevHand = useRef({ r: [0, 0], l: [0, 0] });
  const prevFoot = useRef({ r: [0, 0], l: [0, 0] });
  
  const strikeState = useRef({
    rightHand: { phase: 'idle', startTime: 0 },
    leftHand: { phase: 'idle', startTime: 0 },
    rightFoot: { phase: 'idle', startTime: 0 },
    leftFoot: { phase: 'idle', startTime: 0 }
  });

  const images = useRef({
    gloveHandDerecha: new Image(),
    gloveHandIzquierda: new Image(),
    gloveFoot: new Image(),
    background: new Image(),
    bag: new Image(),
    playerHead: new Image(),
    playerBody: new Image(),
    armImage: new Image(),
    leftArmImage: new Image(),
    rightLegImage: new Image(),
    leftLegImage: new Image()
  });

  useEffect(() => {
    if (!equipmentImages.loading) {
      images.current.gloveHandDerecha.src = equipmentImages.gloveHandDerecha;
      images.current.gloveHandIzquierda.src = equipmentImages.gloveHandIzquierda;
      images.current.gloveFoot.src = equipmentImages.gloveFoot;
      images.current.playerHead.src = equipmentImages.playerHead;
    }
    images.current.background.src = '/img/backgrounddojan.png';
    images.current.bag.src = '/lado/bolsa.png';
    images.current.playerBody.src = '/img/rotura/traje.png';
    images.current.armImage.src = '/img/rotura/brazoderecho.png';
    images.current.leftArmImage.src = '/img/rotura/brazoizquierdo.png';
    images.current.rightLegImage.src = '/img/rotura/piederecho.png';
    images.current.leftLegImage.src = '/img/rotura/pieizquierdo.png';
  }, [equipmentImages]);

  const createParticles = (x, y, color, count = 20) => {
    const newParticles = [];
    const colors = ['#FFD700', '#FFA500', '#FF6B00', '#FFFF00', '#FF4500'];
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count;
      const speed = 3 + Math.random() * 5;
      newParticles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        life: 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 3 + Math.random() * 5,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.3
      });
    }
    particlesRef.current = [...particlesRef.current, ...newParticles];
  };

  const updateParticles = () => {
    particlesRef.current = particlesRef.current.filter(p => {
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.98;
      p.vy += 0.4;
      p.life -= 0.015;
      p.rotation += p.rotationSpeed;
      p.size *= 0.97;
      return p.life > 0;
    });
  };

  const drawParticles = (ctx) => {
    particlesRef.current.forEach(p => {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.globalAlpha = p.life;
      
      // Sombra para efecto glow
      ctx.shadowBlur = 10;
      ctx.shadowColor = p.color;
      
      // Dibujar estrella
      ctx.fillStyle = p.color;
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
        const x = Math.cos(angle) * p.size;
        const y = Math.sin(angle) * p.size;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        
        const innerAngle = angle + Math.PI / 5;
        const innerX = Math.cos(innerAngle) * (p.size * 0.4);
        const innerY = Math.sin(innerAngle) * (p.size * 0.4);
        ctx.lineTo(innerX, innerY);
      }
      ctx.closePath();
      ctx.fill();
      
      ctx.restore();
    });
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
  };

  const drawBag = (ctx, width, height) => {
    const bagX = width * 0.5;
    const bagY = height * 0.60;
    
    // Dibujar imagen de la bolsa
    if (images.current.bag.complete && images.current.bag.naturalWidth > 0) {
      const bagWidth = 250;
      const bagHeight = 400;
      ctx.drawImage(images.current.bag, bagX - bagWidth/2, bagY - bagHeight/2, bagWidth, bagHeight);
    } else {
      ctx.fillStyle = '#8B4513';
      ctx.fillRect(bagX - 80, bagY - 100, 160, 300);
    }
    
    const time = Date.now() / 1000;
    const currentZone = targetZoneRef.current;
    
    // Dibujar todos los círculos
    Object.entries(zones).forEach(([key, zone]) => {
      const zoneX = bagX + (width * zone.xOffset);
      const zoneY = height * zone.y;
      const isActive = key === currentZone;
      
      if (isActive) {
        // Círculo activo con animación
        const pulse = Math.sin(time * 5) * 0.3 + 0.7;
        const glow = Math.sin(time * 4) * 8 + 12;
        
        // Anillo exterior giratorio
        ctx.save();
        ctx.translate(zoneX, zoneY);
        ctx.rotate(time * 2);
        ctx.strokeStyle = `rgba(255, 0, 0, ${pulse * 0.6})`;
        ctx.lineWidth = 3;
        ctx.setLineDash([10, 10]);
        ctx.beginPath();
        ctx.arc(0, 0, 55, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
        
        // Círculo principal rojo
        ctx.shadowBlur = 30 + glow;
        ctx.shadowColor = '#FF0000';
        const gradient = ctx.createRadialGradient(zoneX, zoneY, 0, zoneX, zoneY, 40);
        gradient.addColorStop(0, `rgba(255, 0, 0, ${pulse * 0.3})`);
        gradient.addColorStop(0.7, `rgba(255, 0, 0, ${pulse * 0.6})`);
        gradient.addColorStop(1, `rgba(255, 0, 0, ${pulse})`);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 8;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(zoneX, zoneY, 40 + Math.sin(time * 6) * 4, 0, 2 * Math.PI);
        ctx.stroke();
      } else {
        // Círculos inactivos en gris
        ctx.strokeStyle = 'rgba(136, 136, 136, 0.3)';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc(zoneX, zoneY, 35, 0, 2 * Math.PI);
        ctx.stroke();
      }
    });
    
    ctx.shadowBlur = 0;
  };

  const drawSkeleton = (ctx, landmarks, width, height) => {
    return drawOptimizedSkeleton(ctx, landmarks, width, height, images.current);
  };

  const drawGloves = (ctx, landmarks, width, height) => {
    if (!landmarks) return;
    const shoulderDist = calculateShoulderDistance(landmarks, width);
    drawOptimizedGloves(ctx, landmarks, width, height, images.current, shoulderDist);
  };

  const detectHit = (landmarks, width, height) => {
    if (!landmarks) return;
    
    const rIndex = landmarks[20];
    const lIndex = landmarks[19];
    const rFoot = landmarks[32];
    const lFoot = landmarks[31];
    
    const handR = [rIndex.x, rIndex.y];
    const handL = [lIndex.x, lIndex.y];
    const footR = [rFoot.x, rFoot.y];
    const footL = [lFoot.x, lFoot.y];
    
    const speedRHand = Math.sqrt((handR[0] - prevHand.current.r[0]) ** 2 + (handR[1] - prevHand.current.r[1]) ** 2);
    const speedLHand = Math.sqrt((handL[0] - prevHand.current.l[0]) ** 2 + (handL[1] - prevHand.current.l[1]) ** 2);
    const speedRFoot = Math.sqrt((footR[0] - prevFoot.current.r[0]) ** 2 + (footR[1] - prevFoot.current.r[1]) ** 2);
    const speedLFoot = Math.sqrt((footL[0] - prevFoot.current.l[0]) ** 2 + (footL[1] - prevFoot.current.l[1]) ** 2);
    
    prevHand.current = { r: handR, l: handL };
    prevFoot.current = { r: footR, l: footL };
    
    const now = Date.now();
    if (now - lastHitTime.current < 800) return;
    
    const zone = zones[targetZoneRef.current];
    const bagX = width * 0.5;
    const zoneX = bagX + (width * zone.xOffset);
    const zoneY = height * zone.y;
    
    // Verificar distancia y velocidad para cada extremidad
    const limbs = [
      { name: 'rightHand', x: width * rIndex.x, y: height * rIndex.y, speed: speedRHand },
      { name: 'leftHand', x: width * lIndex.x, y: height * lIndex.y, speed: speedLHand },
      { name: 'rightFoot', x: width * rFoot.x, y: height * rFoot.y, speed: speedRFoot },
      { name: 'leftFoot', x: width * lFoot.x, y: height * lFoot.y, speed: speedLFoot }
    ];
    
    for (const limb of limbs) {
      const dist = Math.sqrt((limb.x - zoneX) ** 2 + (limb.y - zoneY) ** 2);
      
      // Requiere movimiento (velocidad > 0.02) para evitar detección estática
      if (dist < 100 && limb.speed > 0.02) {
        lastHitTime.current = now;
        
        const points = zone.points * (comboRef.current >= 3 ? 2 : 1);
        scoreRef.current += points;
        setScore(scoreRef.current);
        
        createParticles(limb.x, limb.y, '#FFD700', 30);
        if (pegarOponenteRef.current) { pegarOponenteRef.current.currentTime = 0; pegarOponenteRef.current.play().catch(() => {}); }
        
        comboRef.current += 1;
        setComboCount(comboRef.current);
        clearTimeout(comboTimer.current);
        comboTimer.current = setTimeout(() => {
          comboRef.current = 0;
          setComboCount(0);
        }, 2000);
        
        setFeedback({ show: true, text: `+${points}`, type: 'hit', x: limb.x, y: limb.y, startTime: Date.now() });
        
        setTimeout(() => setFeedback({ show: false, text: '', type: '', x: 0, y: 0, startTime: 0 }), 1000);
        
        const zoneKeys = Object.keys(zones);
        let newZone = zoneKeys[Math.floor(Math.random() * zoneKeys.length)];
        // Asegurar que sea diferente a la actual
        while (newZone === targetZone && zoneKeys.length > 1) {
          newZone = zoneKeys[Math.floor(Math.random() * zoneKeys.length)];
        }
        console.log('Cambiando zona de', targetZoneRef.current, 'a', newZone);
        targetZoneRef.current = newZone;
        setTargetZone(newZone);
        
        break;
      }
    }
  };

  const onResults = (results) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || video.videoWidth === 0) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (images.current.background.complete) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(images.current.background, -canvas.width, 0, canvas.width, canvas.height);
      ctx.restore();
    } else {
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    
    drawBag(ctx, canvas.width, canvas.height);
    
    if (results.poseLandmarks) {
      drawSkeleton(ctx, results.poseLandmarks, canvas.width, canvas.height);
      drawGloves(ctx, results.poseLandmarks, canvas.width, canvas.height);
      detectHit(results.poseLandmarks, canvas.width, canvas.height);
    }
    
    updateParticles();
    drawParticles(ctx);
    
    // Dibujar feedback de puntos flotantes AL FINAL
    if (feedback.show) {
      const elapsed = Date.now() - feedback.startTime;
      const progress = Math.min(elapsed / 1000, 1);
      const yOffset = progress * 120;
      const scale = 1 + (progress * 0.3);
      const opacity = 1 - progress;
      
      ctx.save();
      ctx.scale(-1, 1);
      ctx.globalAlpha = opacity;
      ctx.shadowColor = '#FFD700';
      ctx.shadowBlur = 40;
      ctx.fillStyle = '#FFD700';
      ctx.font = `bold ${60 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 8;
      ctx.strokeText(feedback.text, -feedback.x, feedback.y - yOffset);
      ctx.fillText(feedback.text, -feedback.x, feedback.y - yOffset);
      ctx.restore();
    }
    
    ctx.restore();
  };

  onResultsRef.current = onResults;
  const poseLoader = usePoseLoader((...args) => onResultsRef.current?.(...args));

  // Iniciar carga de Pose
  useEffect(() => {
    const checkEnergy = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/training/start`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ energyCost })
        });
        if (!response.ok) {
          setShowEnergyWarning(true);
          setTimeout(onBackToMenu, 3000);
          return;
        }
      } catch (error) {
        console.error('Error al verificar energía:', error);
      }
    };
    checkEnergy();
    if (videoRef.current) poseLoader.start(videoRef.current);
    return () => {
      poseLoader.stop();
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    };
  }, []);

  // Cuando Pose termina de cargar, iniciar countdown y timer
  useEffect(() => {
    if (!poseLoader.loaded) return;
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
    }
    let timer = null;
    const countdownTimer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          setIsReady(true);
          timer = setInterval(() => {
            setTimeLeft(prev => {
              if (prev <= 1) {
                clearInterval(timer);
                handleTrainingEnd();
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timer) clearInterval(timer); clearInterval(countdownTimer); };
  }, [poseLoader.loaded]);

  const handleTrainingEnd = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/training/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          score: scoreRef.current,
          tiempo: 60,
          precision: 0
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Entrenamiento finalizado:', data);
        
        if (data.subiDeNivel && data.beltData) {
          console.log('Promoción de cinturón detectada:', data.cinturon);
          setBeltPromotionData({
            cinturon: data.cinturon,
            beltData: data.beltData
          });
          // Mostrar modal después de un delay
          setTimeout(() => {
            console.log('Mostrando modal de cinturón');
            setShowBeltModal(true);
          }, 2500);
          return; // No volver al menú todavía
        }
      }
    } catch (error) {
      console.error('Error al guardar entrenamiento:', error);
    }
    
    // Solo volver al menú si NO hay promoción de cinturón
    setTimeout(() => {
      if (onBackToMenu) onBackToMenu();
    }, 2000);
  };

  const handleCloseBeltModal = () => {
    setShowBeltModal(false);
    setBeltPromotionData(null);
    if (onBackToMenu) onBackToMenu();
  };

  return (
    <div className="App">
      <div className="game-container">
        <audio ref={audioRef} src="/audio/entrenamiento.mp3" loop />
        <audio ref={pegarOponenteRef} src="/audio/pegaroponente.mp3" preload="auto" />
        <video ref={videoRef} style={{ display: 'none' }} />
        <canvas ref={canvasRef} className="game-canvas" />
        
        {!poseLoader.loaded && (
          <PosePreloader progress={poseLoader.progress} statusText={poseLoader.statusText} title="ENTRENAMIENTO" hint="Dejá el celular en un lugar fijo sin que se caiga. Alejate hasta que te tome el cuerpo completo. Golpeá los círculos rojos con manos o pies." />
        )}
        
        {/* HUD */}
        <button className="back-button" onClick={onBackToMenu}>
          ← VOLVER
        </button>
        
        <div className="training-hud">
          <div className="training-score">
            <div className="score-label">PUNTOS</div>
            <div className="score-value">{score}</div>
          </div>
          <div className="energy-cost">🔋 -10</div>
          <div className={`game-timer ${timeLeft <= 10 ? 'warning' : ''}`}>
            {timeLeft}
          </div>
        </div>

        {!isReady && countdown > 0 && (
          <div className="countdown-overlay">
            <div className="countdown-number">{countdown}</div>
            <div className="countdown-text">¡Prepárate!</div>
          </div>
        )}

        {!isReady && countdown === 0 && (
          <div className="countdown-overlay">
            <div className="countdown-go">¡YA!</div>
          </div>
        )}

        {showEnergyWarning && (
          <div className="training-result">
            <h2>⚠️ ENERGÍA INSUFICIENTE</h2>
            <div className="final-score">Necesitas {energyCost} de energía para entrenar</div>
          </div>
        )}

        {timeLeft === 0 && !showBeltModal && !beltPromotionData && (
          <div className="training-result">
            <h2>¡ENTRENAMIENTO COMPLETADO!</h2>
            <div className="final-score">Puntuación: {score}</div>
          </div>
        )}

        {showBeltModal && beltPromotionData && (
          <BeltPromotionModal
            cinturon={beltPromotionData.cinturon}
            beltData={beltPromotionData.beltData}
            onClose={handleCloseBeltModal}
          />
        )}
      </div>
    </div>
  );
}

export default TrainingMode;
